/**
 * MetricCard - Metric summary card component
 *
 * Used on the dashboard to display secondary metrics with pipeline saturation
 */

import type { ReactNode } from 'react';

export interface MetricCardProps {
  label: string;
  detail: string;
  background: string;
  iconColor: string;
  barGradient: string;
  icon: ReactNode;
  value: number;
  valueDisplay?: string;
  share: number;
  shareLabel?: string;
  footerLabel?: string;
  footerValue?: string;
}

export function MetricCard({
  label,
  detail,
  background,
  iconColor,
  barGradient,
  icon,
  value,
  valueDisplay,
  share,
  shareLabel,
  footerLabel,
  footerValue,
}: MetricCardProps) {
  const normalizedShare = Math.max(0, Math.min(share, 100));

  return (
    <div className="group relative overflow-hidden rounded-xl border border-gray-200/60 bg-white shadow-md shadow-gray-200/40 dark:border-gray-800/50 dark:bg-gray-900">
      <div className={`absolute inset-0 bg-linear-to-br ${background} opacity-80`} />
      <div className="relative flex h-full flex-col gap-6 p-4">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-gray-500 dark:text-gray-400">
            {label}
          </div>
          <div
            className={`flex items-center justify-center rounded-xl bg-white/80 text-base shadow-inner shadow-white/40 dark:bg-white/10 dark:text-white ${iconColor}`}
          >
            {icon}
          </div>
        </div>
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-4xl font-semibold text-gray-900 dark:text-white">
              {valueDisplay ?? value}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-300">{detail}</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              {shareLabel ?? 'Share'}
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{Math.round(normalizedShare)}%</p>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-[11px] font-medium text-gray-400 dark:text-gray-500">
            <span>{footerLabel ?? 'Pipeline saturation'}</span>
            <span>{footerValue ?? (value ? `${value} active` : 'Idle')}</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-black/5 dark:bg-white/10">
            <div
              className={`h-full rounded-full bg-linear-to-r ${barGradient}`}
              style={{ width: `${Math.max(normalizedShare, value ? 12 : 4)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
